// Defines a company
// This object contains the shared meta-info about the company to be shared in other blocks
export class Company {
  status: 'draft' | 'published' | 'disabled'; // Toggle to allow company to be seen publicly
  datePublished: string;
  dateUpdated: string;
  code: string;
  codeLocked: bool;
  officeEnabled: bool;
  employeesEnabled: bool;
  name: string;
  industry: string;
  size: 'small' | 'medium' | 'large';
  featurePhoto: string;
  jobPhoto: string;
  aboutPara: string;
  address: string;
  facebook: string;
  instagram: string;
  linkedin: string;
  gmaps: string;
  logo: string;
  mrt: string;
  previewPrimary: string;
  previewSecondary: string;
  subtitle: string;
  employees: Employee[];

  constructor() {
    this.status = 'draft';
    this.datePublished = '';
    this.dateUpdated = '';
    this.code = '';
    this.codeLocked = false;
    this.officeEnabled = false;
    this.employeesEnabled = false;
    this.name = '';    
    this.industry = '';
    this.size = 'large';
    this.featurePhoto = '';
    this.jobPhoto = '';
    this.aboutPara = '';
    this.address = '';
    this.facebook = '';
    this.instagram = '';
    this.linkedin = '';
    this.gmaps = '';
    this.logo = '';
    this.mrt = '';
    this.previewPrimary = '';
    this.previewSecondary = '';
    this.subtitle = '';
    this.employees = [];
  }
}

// Defines an employee
// This object contains some meta-info about the employee
// The codeLocked bool is for the admin form to process
export class Employee {
  code: string;
  codeLocked: bool;
  fullName: string;
  firstName: string;
  lastName: string;
  position: string;
  gender: string;
  photo: string;

  constructor() {
    this.code = '';
    this.codeLocked = false;
    this.fullName = '';
    this.firstName = '';
    this.lastName = '';
    this.position = '';
    this.gender = '';
    this.photo = '';
  }
}

// Defines a content block
// Content blocks can either be showcase or content type
// Each type has 2 layouts
// Content blocks can have an optional header
// Optional header can be a string or a url link
export class Block {
  type: 'showcase' | 'content';
  layout: 'A' | 'B';
  header: string;
  content: Cell[]

  constructor(type: 'showcase' | 'content', layout: 'A' | 'B', content: Cell[]) {
    this.type = type;
    this.layout = layout;
    this.header = '';
    this.content = content || [];
  }
}

export class Cell {
  type: 'video-medium' | 'video-large' | 'video-huge' | 'photo-small' | 'photo-large' | 'photo-carousel' | 'paragraph-small' | 'paragraph-medium' | 'paragraph-huge' | 'address' | 'none';

  constructor(type: string) {
    this.type = type;
  }
}

// Cell with video; can be either medium (1x2), large (2x2), or huge (3x2)
// Input for the video should be a Vimeo id (e.g. 231662921)
// Comes with an optional caption
export class VideoCell extends Cell {
  video: string;
  caption: string;

  constructor(type: 'video-medium' | 'video-large' | 'video-huge') {
    super(type);
    this.video = '';
    this.caption = '';
  }
}

// Cell with photo; can be either small (1x1) or large (2x2)
// Note: All photos will always come with optional link and optional caption
// Photos with captions will generate an overlay
export class PhotoCell extends Cell {
  photo: string;
  url: string;
  caption: string;

  constructor(type: 'photo-small' | 'photo-large') {
    super(type);
    this.photo = '';
    this.url = '';
    this.caption = '';
  }
}

export class PhotoCarouselCell extends Cell {
  photos: string[];
  captions: string[];

  constructor(type: 'photo-carousel') {
    super(type);
    this.photos = [];
    this.captions = [];
  }
}

// Cell with a paragraph of text. Either small (1x1), medium (1x2, 2x1), or huge (3x2)
// Comes with an optional header
export class ParagraphCell extends Cell {
  header: string;
  text: string;
  
  constructor(type: 'paragraph-small' | 'paragraph-medium' | 'paragraph-huge') {
    super(type);
    this.header = '';
    this.text = '';
  }
}

// Cell with address. Does not need be be filled in.
export class AddressCell extends Cell {
  constructor() {
    super();
    this.type = 'address';
  }
}

export class BlogPostCell extends Cell {
  url: string;
  title: string;
  photo: string;

  constructor(type: 'blogpost') {
    super(type);
    this.url = '';
    this.title = '';
    this.photo = '';
  }
}

export class Job {
  status: 'draft' | 'published' | 'disabled'; // Set to 'published' to allow job to be seen publicly
  datePublished: string;
  dateUpdated: string;
  code: string;
  codeLocked: bool;
  name: string;
  company: string;
  type: 'fulltime' | 'parttime' | 'contract' | 'internship';
  department: string;
  country: string;
  experienceRequired: boolean;
  targetExperience: string;
  targetNationality: string;
  targetPreference: string;
  startDate: string;
  endDate: string;
  overseasPoints: string;
  aboutPara: string;
  rolePara: string;
  rolePoints: string;
  reqPara: string;
  reqPoints: string;

  constructor() {
    this.status = 'draft';
    this.datePublished = '';
    this.dateUpdated = '';
    this.code = '';
    this.codeLocked = false;
    this.name = '';
    this.company = '';
    this.type = 'fulltime';
    this.department = '';
    this.country = '';
    this.experienceRequired = false;
    this.targetExperience = '';
    this.targetNationality = '';
    this.targetPreference = '';
    this.startDate = '';
    this.endDate = '';
    this.overseasPoints = '';
    this.aboutPara = '';
    this.aboutPara = '';
    this.rolePara = '';
    this.rolePoints = '';
    this.reqPara = '';
    this.reqPoints = '';
  }
}



// WEBPACK FOOTER //
// ./src/utils/variables.js